import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaTruck, FaWeightHanging, FaStar, FaMapMarkerAlt, FaRoute, FaArrowRight } from 'react-icons/fa';
import KaroTrukkLogo from '../Common/KaroTrukkLogo';
import { motion } from 'framer-motion';
import axios from 'axios';
import api from '../../utils/api';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || process.env.VITE_MAPBOX_ACCESS_TOKEN;

const getDocLink = (path) => {
    if (!path) return '';
    if (path.startsWith('data:') || path.startsWith('http://') || path.startsWith('https://') || path.startsWith('blob:')) {
        return path;
    }
    const normalizedPath = path.replace(/\\/g, '/');
    const baseUrl = process.env.REACT_APP_API_URL || '';
    if (baseUrl) {
        return `${baseUrl}${normalizedPath.startsWith('/') ? '' : '/'}${normalizedPath}`;
    }
    return `${normalizedPath.startsWith('/') ? '' : '/'}${normalizedPath}`;
};

const CATEGORIES = [
    { id: 'HEAVY', label: 'Heavy Trucks & Trailers', icon: FaTruck },
    { id: 'CONSTRUCTION', label: 'Construction Vehicles', icon: FaWeightHanging },
    { id: 'EQUIPMENT', label: 'Equipment Rentals', icon: FaTruck },
    { id: 'TANKER', label: 'Water Tankers', icon: FaTruck }
];

const QuoteResultsPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const pickup = searchParams.get('pickup') || '';
    const drop = searchParams.get('drop') || '';

    const [loading, setLoading] = useState(true);
    const [distanceKm, setDistanceKm] = useState(null);
    const [groupedVehicles, setGroupedVehicles] = useState({
        HEAVY: [], CONSTRUCTION: [], EQUIPMENT: [], TANKER: []
    });

    const fetchData = React.useCallback(async () => {
        setLoading(true);
        try {
            // 1. Calculate distance using Mapbox
            let dist = 100; // Fallback distance 100km
            if (MAPBOX_TOKEN) {
                try {
                    const pickupRes = await axios.get(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(pickup)}.json?country=IN&access_token=${MAPBOX_TOKEN}&limit=1`);
                    const dropRes = await axios.get(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(drop)}.json?country=IN&access_token=${MAPBOX_TOKEN}&limit=1`);
                    
                    if (pickupRes.data.features.length > 0 && dropRes.data.features.length > 0) {
                        const pCoords = pickupRes.data.features[0].center; // [lng, lat]
                        const dCoords = dropRes.data.features[0].center;
                        
                        const dirRes = await axios.get(`https://api.mapbox.com/directions/v5/mapbox/driving/${pCoords[0]},${pCoords[1]};${dCoords[0]},${dCoords[1]}?access_token=${MAPBOX_TOKEN}`);
                        if (dirRes.data.routes && dirRes.data.routes.length > 0) {
                            dist = dirRes.data.routes[0].distance / 1000;
                        }
                    }
                } catch (geoErr) {
                    console.error("Geocoding failed, using fallback distance", geoErr);
                }
            }
            setDistanceKm(dist);

            // 2. Fetch public vehicles
            const response = await api.get('/api/public/vehicles');
            let allVehicles = [];
            if (response.data.success) {
                allVehicles = response.data.data || [];
                // Filter only those with perKm pricing
                allVehicles = allVehicles.filter(v => v.pricing && v.pricing.perKm && v.pricing.perKm > 0);
                // Sort by rating (highest first)
                allVehicles.sort((a, b) => (b.rating?.average || 0) - (a.rating?.average || 0));
            }

            // Group by category, limit to 4 per category
            const groups = { HEAVY: [], CONSTRUCTION: [], EQUIPMENT: [], TANKER: [] };
            CATEGORIES.forEach(cat => {
                groups[cat.id] = allVehicles.filter(v => v.category === cat.id).slice(0, 4);
            });
            
            setGroupedVehicles(groups);
        } catch (error) {
            console.error("Error fetching quotes:", error);
        } finally {
            setLoading(false);
        }
    }, [pickup, drop]);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (!pickup || !drop) {
            // Redirect back if missing params
            navigate('/');
            return;
        }
        fetchData();
    }, [pickup, drop, fetchData, navigate]);

    const handleLoginTrigger = () => {
        // Force redirect to login page for unauthenticated users trying to interact
        navigate('/login', { state: { role: 'client' } });
    };

    return (
        <div className="min-h-screen bg-[#F8F9FA] font-sans flex flex-col relative z-0 text-gray-900">
            {/* Navbar */}
            <div className="bg-black text-white px-6 py-5 flex items-center justify-between shadow-lg sticky top-0 z-50 shrink-0">
                <div className="flex items-center">
                    <button onClick={() => navigate('/')} className="mr-4 text-white hover:text-gray-300 transition-colors p-1.5 hover:bg-white/10 rounded-full">
                        <FaArrowLeft />
                    </button>
                    <Link to="/" aria-label="KaroTrukk Home" className="flex items-center">
                        <KaroTrukkLogo className="h-6 w-auto hover:opacity-90 transition-opacity" color="#FFC72C" />
                    </Link>
                </div>
                <button onClick={handleLoginTrigger} className="text-sm font-bold bg-[#F9B101] text-black px-4 py-2 rounded-xl hover:bg-[#e09f00] transition">
                    Sign In
                </button>
            </div>

            {/* Hero Section */}
            <div className="bg-black border-b border-gray-800 py-8 px-6">
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-2xl md:text-3xl font-black text-white mb-6">Your Price Estimates</h2>
                    
                    <div className="relative p-5 bg-white/10 rounded-2xl border border-white/10 backdrop-blur-md">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-0">
                            {/* Pickup */}
                            <div className="flex items-center gap-3 z-10 md:flex-1">
                                <div className="w-8 h-8 rounded-full bg-[#F9B101]/20 text-[#F9B101] flex items-center justify-center shrink-0 text-sm z-10">
                                    <FaMapMarkerAlt />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Pickup</p>
                                    <p className="text-base font-bold text-white truncate capitalize">{pickup}</p>
                                </div>
                            </div>
                            
                            {/* Mobile Connecting Line & Distance */}
                            <div className="md:hidden flex items-center gap-4 pl-[15px] -my-2 relative z-0">
                                <div className="w-0.5 h-10 bg-gray-600/50"></div>
                                <div className="text-[10px] text-gray-300 font-bold bg-white/10 px-2.5 py-1 rounded-lg border border-white/10 flex items-center gap-1.5">
                                    <FaRoute className="text-gray-400" />
                                    {distanceKm ? `${Math.round(distanceKm)} km` : '...'}
                                </div>
                            </div>
                            
                            {/* Desktop Distance */}
                            <div className="hidden md:flex flex-col items-center px-4 flex-1">
                                <div className="w-full flex items-center justify-center gap-2 mb-1">
                                    <div className="h-[1px] bg-gray-600 flex-1"></div>
                                    <FaRoute className="text-gray-400 text-lg" />
                                    <div className="h-[1px] bg-gray-600 flex-1"></div>
                                </div>
                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                                    {distanceKm ? `${Math.round(distanceKm)} kilometers` : 'Calculating...'}
                                </span>
                            </div>
                            
                            {/* Drop */}
                            <div className="flex items-center gap-3 md:justify-end z-10 md:flex-1">
                                {/* Desktop: Text then Icon */}
                                <div className="hidden md:block text-right min-w-0">
                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Drop</p>
                                    <p className="text-base font-bold text-white truncate capitalize">{drop}</p>
                                </div>
                                
                                <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center shrink-0 text-sm z-10">
                                    <FaMapMarkerAlt />
                                </div>
                                
                                {/* Mobile: Icon then Text */}
                                <div className="md:hidden min-w-0">
                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Drop</p>
                                    <p className="text-base font-bold text-white truncate capitalize">{drop}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-6 py-12 flex-1 w-full space-y-16">
                {loading ? (
                    <div className="text-center py-20">
                        <div className="w-12 h-12 border-4 border-[#F9B101] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-gray-500 font-bold tracking-wide">Calculating route and fetching top-rated fleets...</p>
                    </div>
                ) : (
                    CATEGORIES.map(category => {
                        const vehicles = groupedVehicles[category.id];
                        if (!vehicles || vehicles.length === 0) return null; // Skip empty categories

                        return (
                            <div key={category.id} className="space-y-6">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-200 pb-4">
                                    <h3 className="text-xl md:text-2xl font-black text-gray-900 flex items-start sm:items-center gap-3">
                                        <category.icon className="text-[#9B6E00] shrink-0 mt-1 sm:mt-0" />
                                        <span className="leading-tight">{category.label}</span>
                                    </h3>
                                    <button 
                                        onClick={handleLoginTrigger}
                                        className="text-[#9B6E00] font-bold text-sm hover:text-[#7A5700] flex items-center gap-1 group self-start sm:self-auto pl-8 sm:pl-0"
                                    >
                                        View More <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </div>

                                <div 
                                    className="flex md:grid md:grid-cols-2 lg:grid-cols-4 overflow-x-auto gap-4 md:gap-6 pb-6 snap-x snap-mandatory -mx-6 px-6 md:mx-0 md:px-0 scroll-smooth"
                                >
                                    {vehicles.map((v, i) => {
                                        const approxPrice = distanceKm ? (distanceKm * v.pricing.perKm) : 0;
                                        const photoPath = v.photos?.front || v.photos?.side || v.photos?.leftSide || v.photos?.rightSide || v.photos?.rear || v.photos?.closeUp || v.imageUrl;
                                        const photoUrl = getDocLink(photoPath);
                                        
                                        return (
                                            <motion.div 
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.4, delay: i * 0.1 }}
                                                key={v._id} 
                                                className="flex-none w-[85%] md:w-auto bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 flex flex-col overflow-hidden group snap-center"
                                            >
                                                {/* Image */}
                                                <div className="h-32 bg-gray-50 flex items-center justify-center relative border-b border-gray-100 overflow-hidden">
                                                    <div className="absolute top-2 right-2 bg-white/90 backdrop-blur text-black px-2 py-1 rounded-md text-[10px] font-bold shadow-sm flex items-center gap-1 z-10">
                                                        <FaStar className="text-yellow-500" size={10} /> 
                                                        {v.rating?.average ? v.rating.average.toFixed(1) : '5.0'}
                                                    </div>
                                                    {photoUrl ? (
                                                        <img src={photoUrl} alt={v.model} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.target.style.display = 'none'; }} />
                                                    ) : (
                                                        <FaTruck size={36} className="text-gray-200 group-hover:scale-110 transition-transform duration-500" />
                                                    )}
                                                </div>

                                                {/* Body */}
                                                <div className="p-4 flex-1 flex flex-col">
                                                    <h4 className="text-base font-black text-gray-900 mb-0.5 truncate" title={v.model}>{v.model}</h4>
                                                    <p className="text-[10px] text-gray-500 font-bold mb-3 flex items-center gap-1 truncate">
                                                        {v.ownerId?.businessName || v.ownerId?.name || 'Verified Fleet Partner'} <span className="w-3 h-3 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-[8px]" title="Verified Partner">✓</span>
                                                    </p>

                                                    <div className="bg-gray-50 rounded-xl p-3 mb-4 border border-gray-100">
                                                        <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mb-1">Estimated Price</p>
                                                        <p className="text-xl font-black text-black">
                                                            {approxPrice > 0 ? `₹${Math.round(approxPrice).toLocaleString('en-IN')}` : '₹--'}
                                                        </p>
                                                        <p className="text-[10px] text-gray-500 mt-0.5 font-medium">
                                                            Based on ₹{v.pricing.perKm}/km
                                                        </p>
                                                    </div>

                                                    <div className="mt-auto">
                                                        <button 
                                                            onClick={handleLoginTrigger}
                                                            className="w-full bg-black text-white py-2.5 rounded-lg font-bold hover:bg-neutral-800 transition-colors text-sm shadow-sm active:scale-95"
                                                        >
                                                            Get Quote & Book
                                                        </button>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })
                )}
                
                {!loading && Object.values(groupedVehicles).every(arr => arr.length === 0) && (
                    <div className="text-center py-20">
                        <p className="text-xl text-gray-500 font-bold">No verified fleets with per/km pricing available right now.</p>
                        <button onClick={() => navigate('/')} className="mt-6 text-[#9B6E00] font-bold hover:underline">Return to Home</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QuoteResultsPage;
